import Sidebar from "@/components/Sidebar/Sidebar";
import Header from "@/components/Tax Pro Only/Header/Header";
import {
  AppShell,
  Burger,
  Button,
  Center,
  Group,
  Paper,
  Skeleton,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import React, { Suspense, useEffect, useMemo } from "react";
import ProfileContent from "./components/ProfileContent";
import ProfileHeader from "./components/ProfileHeader";
import { useProfileStore } from "./store/profileStore";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconReload,
} from "@tabler/icons-react";
import { useParams } from "react-router-dom";
import type { AxiosError } from "axios";
import OneClickLoader from "./components/OneClickLoader";
import { pollJob } from "@/utils/api/jobApi";
import { showNotification } from "@mantine/notifications";
import { FormProvider, useForm } from "react-hook-form";
import MinimizeButton from "./components/Sidebar/MinimizeButton";
import { ExtractionSelectionManager } from "./components/Sidebar/ExtractionSelectionManager";
import { useAuthStore } from "@utils/stores/authStore";
import MainHeader from "@/components/Tax Client Only/MainHeader/MainHeader";

const ClientProfile: React.FC = () => {
  const { userId, taxYear } = useParams<{ userId: string; taxYear: string }>();
  const [opened, { toggle }] = useDisclosure(false);
  const { role } = useAuthStore();

  // Use optimized store selectors to reduce re-renders
  const {
    condensed,
    loaderVisible,
    setLoaderVisible,
    job,
    setReview,
    reset,
    invalidateOCRData,
    loading,
    error,
    fetchProfile,
    refetch,
  } = useProfileStore();

  const methods = useForm({
    defaultValues: {}, // We'll populate dynamically after loading
  });

  // Fetch profile data ONLY on mount or when userId/taxYear URL params change
  // activeYear changes from store should NOT trigger refetch
  useEffect(() => {
    console.log("[ClientProfile] Fetch effect triggered:", {
      userId,
      taxYear,
    });
    if (userId) {
      const yearToFetch = taxYear ? parseInt(taxYear, 10) : undefined;
      console.log(
        "[ClientProfile] Calling fetchProfile with year:",
        yearToFetch
      );
      fetchProfile(userId, yearToFetch);
    }
  }, [userId, taxYear, fetchProfile]);

  // Optimized job polling with better error handling
  useEffect(() => {
    if (!job) return;

    let isMounted = true;
    const POLL_INTERVAL = 5; // seconds
    const POLL_TIMEOUT = 3600; // seconds

    const startJobPolling = async () => {
      try {
        // If job is already completed, handle it immediately
        if (job.status === "completed") {
          setLoaderVisible(false);
          setReview(true);
          await refetch();
          invalidateOCRData();
          useProfileStore.setState({ job: null }); // Clear the job
          return;
        }

        // If job is already failed, handle it immediately
        if (job.status === "failed") {
          setLoaderVisible(false);
          useProfileStore.setState({ job: null }); // Clear the job
          showNotification({
            title: "Error",
            message: "Job failed. Please try again.",
            color: "red",
            icon: <IconAlertCircle size={16} />,
          });
          return;
        }

        // Start polling for job completion
        const finalStatus = await pollJob(job.id, POLL_INTERVAL, POLL_TIMEOUT);

        if (!isMounted) return;

        if (finalStatus === "completed") {
          setLoaderVisible(false);
          setReview(true);
          await refetch();
          invalidateOCRData();
          useProfileStore.setState({ job: null }); // Clear the job
        } else if (finalStatus === "failed") {
          setLoaderVisible(false);
          useProfileStore.setState({ job: null }); // Clear the job
          showNotification({
            title: "Error",
            message: "Job failed. Please try again.",
            color: "red",
            icon: <IconAlertCircle size={16} />,
          });
        }
      } catch (error) {
        if (!isMounted) return;

        console.error("Error polling job status:", error);

        setLoaderVisible(false);
        useProfileStore.setState({ job: null }); // Clear the job
        showNotification({
          title: "Error",
          message: "Failed to check job status. Please try again.",
          color: "red",
          icon: <IconAlertCircle size={16} />,
        });
      }
    };

    startJobPolling();

    return () => {
      isMounted = false;
    };
  }, [job, refetch, invalidateOCRData, setLoaderVisible, setReview]);

  // Clean up state on unmount
  useEffect(() => {
    console.log("[ClientProfile] Component mounted");
    return () => {
      console.log("[ClientProfile] Component unmounting, resetting state");
      reset(); // Reset state when component unmounts
    };
  }, [reset]);

  const renderErrorState = (error: AxiosError): JSX.Element => (
    <Center className="error-container" mih="50vh">
      <Stack align="center" gap="sm" className="error-content">
        <IconAlertTriangle size={48} className="error-icon" color="red" />
        <Text size="xl" fw={500} className="error-title">
          Failed to Load Client Profile
        </Text>
        <Text c="dimmed" className="error-message" ta="center">
          {error.response?.status === 404
            ? "Client not found. Please check the client ID."
            : "We couldn't load the client profile. Please check your connection and try again."}
        </Text>
        <Button
          variant="outline"
          color="blue"
          mt="md"
          leftSection={<IconReload size={18} />}
          onClick={() => refetch()}
          loading={loading}
        >
          Retry
        </Button>
      </Stack>
    </Center>
  );

  // Memoized loading skeleton to prevent unnecessary re-renders
  const loadingSkeleton = useMemo(
    () => (
      <Stack gap="md">
        <Paper withBorder p="md" radius="md">
          <Group justify="space-between" mb="xl">
            <Skeleton height={40} circle />
            <Group>
              <Skeleton height={32} width={100} />
              <Skeleton height={32} width={100} />
            </Group>
          </Group>
          <Group>
            <Skeleton height={80} width={80} circle />
            <Stack>
              <Skeleton height={24} width={200} />
              <Skeleton height={16} width={150} />
              <Skeleton height={16} width={180} />
            </Stack>
          </Group>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Stack>
            <Skeleton height={28} width="40%" mb="xl" />
            <Skeleton height={16} width="90%" />
            <Skeleton height={16} width="80%" />
            <Skeleton height={16} width="85%" mt="sm" />
            <Skeleton height={16} width="75%" />
            <Group mt="xl" gap="sm">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton
                  key={`skeleton-${i}`}
                  height={32}
                  width={100}
                  radius="xl"
                />
              ))}
            </Group>
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="md" mt="md">
          <Skeleton height={28} width="30%" mb="md" />
          <Stack gap="sm">
            <Skeleton key="skeleton-0" height={50} radius="sm" />
            <Skeleton key="skeleton-1" height={50} radius="sm" />
            <Skeleton key="skeleton-2" height={50} radius="sm" />
          </Stack>
        </Paper>
      </Stack>
    ),
    []
  );

  return (
    <FormProvider {...methods}>
      <AppShell
        className="client-page"
        header={{ height: 87 }}
        navbar={{
          width: condensed ? "5rem" : "15.375rem",
          breakpoint: "sm",
          collapsed: { mobile: !opened },
        }}
      >
        <AppShell.Header className="app-header">
          <Burger
            className="burger"
            opened={opened}
            onClick={toggle}
            hiddenFrom="sm"
            size="sm"
          />
          {/* Render the header based on user role */}
          <Suspense fallback={<Skeleton height={87} width="100%" />}>
            {role === "client" ? (
              <MainHeader opened={opened} toggle={toggle} />
            ) : (
              <Header />
            )}
          </Suspense>
        </AppShell.Header>

        <AppShell.Navbar
          className={`nav ${condensed ? "condensed" : ""}`}
          style={{ width: condensed ? "5rem" : "15.375rem" }}
        >
          <Suspense
            fallback={
              <Skeleton
                width={condensed ? "5rem" : "15.375rem"}
                height="100vh"
              />
            }
          >
            <Sidebar condensed={condensed} />
          </Suspense>
        </AppShell.Navbar>

        <AppShell.Main
          style={{
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {loading ? (
            loadingSkeleton
          ) : error ? (
            renderErrorState(error)
          ) : (
            <>
              <ProfileHeader />
              <MinimizeButton />
              <ProfileContent />
            </>
          )}

          {/* One Click Loader */}
          <OneClickLoader visible={loaderVisible} />

          {/* OCR Extraction Selection Manager */}
          <ExtractionSelectionManager />
        </AppShell.Main>
      </AppShell>
    </FormProvider>
  );
};

export default ClientProfile;
